/**
 * トップ画面の必須データ（会員状態・進捗）の判定（指示書 A-1③-1、A-11・A-23）。
 *
 * 以前は、会員状態APIの通信エラー（HTTP 500 等）を「このプランでは利用できません」と表示し、マイページへ送っていた。
 * 通信の失敗と、正常に取得して利用不可と分かった場合を分ける。失敗のときは練習を始めさせない。
 */

/**
 * learner-membership-status の応答を分類する。
 * @param {{ ok: boolean, json: any }} p ok: HTTP 2xx か / json: 解析できた本文（できなければ null）
 * @returns {'allowed'|'denied'|'error'}
 *   allowed … 利用できる
 *   denied  … 正常に取得して、利用できないと分かった（契約終了など → マイページへ案内）
 *   error   … 取得できなかった（通信・サーバーの問題）。利用不可と断定しない
 */
export function classifyMembershipResponse({ ok, json }) {
  if (!ok) return 'error'
  if (!json || typeof json !== 'object') return 'error'
  if (json.can_access === true) return 'allowed'
  if (json.can_access === false) return 'denied'
  return 'error'
}

const isValidDay = (v) => Number.isInteger(v) && v >= 1

/**
 * 現在のDayを決める（進級チェックのRPC → だめなら保存済みの current_day を読む）。
 * どちらからも正しいDayを得られなければ例外を投げる（仮の Day1 にしない。レビュー指摘1）。
 *
 * @param {{ callRpc: () => Promise<{ data: any, error: any }>, readStoredDay: () => Promise<{ data: any, error: any }> }} p
 *   readStoredDay の data は { current_day } を想定
 * @returns {Promise<number>}
 */
export async function loadCurrentDay({ callRpc, readStoredDay }) {
  let rpc = null
  try {
    rpc = await callRpc()
  } catch (e) {
    rpc = { data: null, error: e }
  }
  if (!rpc?.error && isValidDay(rpc?.data)) return rpc.data

  const stored = await readStoredDay()
  if (stored?.error) {
    const err = new Error('current_day の取得に失敗しました')
    err.cause = stored.error
    throw err
  }
  const day = Number(stored?.data?.current_day)
  if (!isValidDay(day)) throw new Error('current_day を取得できませんでした')
  return day
}

/** 練習を始めてよいか。読み込み中・必須データ未確定（失敗を含む）のときは始めない */
export function canStartPractice({ loading, requiredDataReady }) {
  return !loading && requiredDataReady === true
}

/**
 * Top の練習エリアの表示（必須データの読み込みの状態）。
 *   'loading' 読み込み中（スピナーと「練習の準備をしています…」）
 *   'error'   失敗・期限切れ（スピナーを止めて、再試行の案内）
 *   'switch'  選んでいる子は利用できないが、利用できる兄弟がいる（切り替えのボタン）
 *   'ready'   必須データが確定（通常の表示）
 *   'idle'    それ以外（利用できないと分かってマイページへ案内した後など。何も出さない）
 * コメント情報の後読みは含めない。
 */
export function requiredDataView({ loading, requiredDataReady, requiredDataError, switchOffer = null }) {
  if (requiredDataError && !loading) return 'error'
  // 選んでいる子は利用できないが、利用できる兄弟がいる（切り替えのボタン。指示書 B）
  if (switchOffer && !loading) return 'switch'
  if (loading) return 'loading'
  if (requiredDataReady === true) return 'ready'
  return 'idle'
}

/** Day の表示。必須データが確定するまでは「Day —」（前の学習者の Day や仮の Day1 を出さない） */
export function dayLabel({ requiredDataReady, currentDay }) {
  return requiredDataReady === true && Number.isInteger(currentDay) ? `Day ${currentDay}` : 'Day —'
}
